import { useEffect, useState } from "react";
import useConfirm from "../../../hook/useConfirm";
import { formatUSPhone, getUSPhoneDigits } from "../../../utils/phoneFormatter";
import axios from "axios";
import { baseURL } from "../../../config/api";


type CustomerProps = {
    primaryKey: number | null;
    customerId: number | null;
    setCustomerId: (value: number | null) => void;
    customerFirstName: string;
    setCustomerFirstName: (value: string) => void;
    customerLastName: string;
    setCustomerLastName: (value: string) => void;
    customerEmail: string;
    setCustomerEmail: (value: string) => void;
    customerPhoneNumber: string;
    setCustomerPhoneNumber: (value: string) => void;
    customerAdditionalInfo: string;
    status: string,
    goToNextPage: () => void;
    updateAppointmentStatusCustomer: (value: string) => void;
    setPleaseWaitPopup: (value: boolean) => void;
    getDataFromCustomer: (
        firstName: string,
        lastName: string,
        email: string,
        phoneNumber: string,
        additionalInfo: string
    ) => void;
}

export const Customer = ({
    primaryKey,
    customerId,
    setCustomerId,
    customerFirstName,
    setCustomerFirstName,
    customerLastName,
    setCustomerLastName,
    customerEmail,
    setCustomerEmail,
    customerPhoneNumber,
    setCustomerPhoneNumber,
    customerAdditionalInfo,
    status,
    updateAppointmentStatusCustomer,
    setPleaseWaitPopup,
    goToNextPage,
    getDataFromCustomer,
}: CustomerProps) => {
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false);
    const [existingCustomerPhoneNumber, setExistingCustomerPhoneNumber] = useState<string>("");
    const [isExistingPhoneValid, setIsExistingPhoneValid] = useState<boolean>(false);
    const [customerNotExist, setCustomerNotExist] = useState<boolean>(false);
    const [additionalInfo, setAdditionalInfo] = useState<string>("");

    useEffect(() => {
        if (customerId && customerId > 0) {
            setFirstName(customerFirstName);
            setLastName(customerLastName);
            setEmail(customerEmail);
            setPhoneNumber(customerPhoneNumber);
            setAdditionalInfo(customerAdditionalInfo);
        }
    }, [customerId, customerFirstName, customerLastName, customerEmail, customerPhoneNumber, customerAdditionalInfo])

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (existingCustomerPhoneNumber.trim() !== "") {
            timer = setTimeout(() => {
                const valid = /^\d{10}$/.test(existingCustomerPhoneNumber);
                setIsExistingPhoneValid(valid);
            }, 500);
        } else {
            setIsExistingPhoneValid(true);
        }

        return () => clearTimeout(timer);
    }, [existingCustomerPhoneNumber]);

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (phoneNumber.trim() !== "") {
            timer = setTimeout(() => {
                const valid = /^\d{10}$/.test(phoneNumber);
                setIsPhoneValid(valid);
            }, 500);
        } else {
            setIsPhoneValid(true);
        }

        return () => clearTimeout(timer);
    }, [phoneNumber]);


    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setEmail(value);
        const isValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/gm.test(value);
        e.target.setCustomValidity(isValid ? "" : "Please enter a valid email");
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!primaryKey || status === "Scheduled" || status === "Queued" || status === "InProgress") {
            getDataFromCustomer(firstName, lastName, email, phoneNumber, additionalInfo);
        }
        else {
            goToNextPage()
        }
    }

    const handleGetExistingCustomer = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWaitPopup(true);
        axios.get(`${baseURL}customers/GetCustomerByPhone/${existingCustomerPhoneNumber}`)
            .then((res) => {
                setPleaseWaitPopup(false);
                setCustomerNotExist(false);
                setCustomerId(res?.data?.customerID)
                setCustomerFirstName(res?.data?.firstName);
                setCustomerLastName(res?.data?.lastName);
                setCustomerPhoneNumber(res?.data?.phone);
                setCustomerEmail(res?.data?.email);
            })
            .catch((error) => {
                setPleaseWaitPopup(false)
                setCustomerNotExist(true)
                console.error(error)
            })


    }

    const handleNoShow = () => {
        setConfirmPopup(true);
        setConfirmData({
            title: "Are you sure want to update status of this appointment to noshow.",
            confirmBtn: "Yes",
            isConfirm: (callback: any) => {
                updateAppointmentStatusCustomer("noshow");
                callback();
            }
        })
    }

    const handleCancel = () => {
        setConfirmPopup(true);
        setConfirmData({
            title: "Are you sure want to update status of this appointment to cancelled.",
            confirmBtn: "Yes",
            isConfirm: (callback: any) => {
                updateAppointmentStatusCustomer("cancel");
                callback();
            }
        })
    }

    const handleReSchedule = () => {
        setConfirmPopup(true);
        setConfirmData({
            title: "Are you sure want to update status of this appointment to scheduled.",
            confirmBtn: "Yes",
            isConfirm: (callback: any) => {
                updateAppointmentStatusCustomer("scheduled");
                callback();
            }
        })
    }

    return (
        <div className="w-full mt-2">
            <div className={`w-full pb-4`}>
                <div className="flex">
                    <div className="mt-2 p-2 w-[50%]">
                        <div className="w-full pb-4">
                            <span className="text-sm font-semibold text-gray-800">
                                Find Existing Customer
                            </span>
                            <div className="w-full mt-2">
                                <form className="space-y-4" onSubmit={handleGetExistingCustomer}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1">
                                                Phone Number*
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter phone number"
                                                className="w-full border border-gray-300 rounded-md p-3 text-sm outline-none focus:border-primary"
                                                required
                                                value={formatUSPhone(existingCustomerPhoneNumber)}
                                                pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                                                title="Please Enter a 10 digits numbers."
                                                onChange={(e) => {
                                                    const digits = getUSPhoneDigits(e.target.value);
                                                    setExistingCustomerPhoneNumber(digits);
                                                }}
                                            />

                                            {!isExistingPhoneValid && (
                                                <div className="flex items-center gap-1 mt-2 text-xs text-red-500 ml-1">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 26 26"
                                                        strokeWidth={2}
                                                        stroke="currentColor"
                                                        className="w-4 h-4 text-red-500"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M12 9v3m0 3h.01m-9 0a9 9 0 1118 0 9 9 0 01-18 0z"
                                                        />
                                                    </svg>

                                                    <span>Please enter a valid 10-digit phone number</span>
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                    {
                                        customerNotExist && (
                                            <div className="text-sm text-red-500">We can't find any customer with this phonenumber.</div>
                                        )
                                    }
                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary hover:bg-orange-600 text-white cursor-pointer transition shadow-md"
                                    >
                                        Find Customer
                                    </button>
                                </form>
                            </div>
                        </div>
                        <div className="w-full pb-4">
                            <span className="text-sm font-semibold text-gray-800">
                                Customer Personal Details
                            </span>
                            <div className="w-full mt-2">
                                <form className="space-y-4" onSubmit={handleSubmit}>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1">
                                                First Name*
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter first name"
                                                className="w-full border border-gray-300 rounded-md p-3 text-sm outline-none focus:border-primary"
                                                required
                                                value={firstName}
                                                onChange={(e) => { setFirstName(e.target.value) }}
                                            />
                                        </div>

                                        {/* Last Name */}
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1">
                                                Last Name*
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter last name"
                                                className="w-full border border-gray-300 rounded-md p-3 text-sm outline-none focus:border-primary"
                                                required
                                                value={lastName}
                                                onChange={(e) => { setLastName(e.target.value) }}
                                            />
                                        </div>
                                    </div>



                                    {/* Email */}
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-1">
                                            Email*
                                        </label>
                                        <input
                                            type="email"
                                            placeholder="Enter email address"
                                            className="w-full border border-gray-300 rounded-md p-3 text-sm outline-none focus:border-primary"
                                            required
                                            value={email}
                                            onChange={handleEmailChange}
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-1">
                                            Phone Number*
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter phone number"
                                            className="w-full border border-gray-300 rounded-md p-3 text-sm outline-none focus:border-primary"
                                            required
                                            value={formatUSPhone(phoneNumber)}
                                            pattern="[\(][0-9]{3}[\)][\s]{1}[0-9]{3}[\-]{1}[0-9]{4}"
                                            title="Please Enter a 10 digits numbers."
                                            onChange={(e) => {
                                                const digits = getUSPhoneDigits(e.target.value);
                                                setPhoneNumber(digits);
                                            }}
                                        />
                                        {!isPhoneValid && (
                                            <div className="flex items-center gap-1 mt-2 text-xs text-red-500 ml-1">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 26 26"
                                                    strokeWidth={2}
                                                    stroke="currentColor"
                                                    className="w-4 h-4 text-red-500"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 9v3m0 3h.01m-9 0a9 9 0 1118 0 9 9 0 01-18 0z"
                                                    />
                                                </svg>

                                                <span>Please enter a valid 10-digit phone number</span>
                                            </div>
                                        )}

                                    </div>

                                    {/* Extra Information (Optional) */}
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-1">
                                            Additional Information (Optional)
                                        </label>
                                        <textarea
                                            rows={4}
                                            placeholder="Add any extra details about your service request..."
                                            className="w-full border border-gray-300 rounded-md p-3 text-sm outline-none focus:border-primary"
                                            value={additionalInfo}
                                            onChange={(e) => { setAdditionalInfo(e.target.value) }}
                                        />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        {
                                            (!primaryKey || (status !== "NoShow" && status !== "Cancelled")) && (
                                                <button
                                                    type="submit"
                                                    className="w-full sm:w-auto px-4 py-2 rounded bg-primary hover:bg-orange-600 text-white cursor-pointer transition shadow-md"
                                                >
                                                    {primaryKey !== null && primaryKey > 0 ? "Next" : "Confirm  "}
                                                </button>
                                            )
                                        }

                                        {
                                            ((primaryKey !== null && primaryKey > 0) && status === "Scheduled") && (
                                                <button
                                                    type="button"
                                                    onClick={handleNoShow}
                                                    className="w-full sm:w-auto px-4 py-2 rounded bg-gray-500 hover:bg-gray-600 text-white transition shadow-md"
                                                >
                                                    No Show
                                                </button>
                                            )
                                        }

                                        {
                                            ((primaryKey !== null && primaryKey > 0) && (status === "Scheduled" || status === "NoShow")) && (
                                                <button
                                                    type="button"
                                                    onClick={handleCancel}
                                                    className="w-full sm:w-auto px-4 py-2 rounded bg-red-500 hover:bg-red-600 text-white cursor-pointer transition shadow-md"
                                                >
                                                    Cancel
                                                </button>
                                            )
                                        }


                                        {
                                            ((primaryKey !== null && primaryKey > 0) && (status === "NoShow" || status === "Cancelled")) && (
                                                <button
                                                    type="button"
                                                    onClick={handleReSchedule}
                                                    className="w-full sm:w-auto px-4 py-2 rounded bg-primary hover:bg-orange-600 text-white cursor-pointer transition shadow-md"
                                                >
                                                    ReSchedule
                                                </button>
                                            )
                                        }

                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    )
}